import type { SVGProps } from 'react';

/**
 * Jeu d'icônes en traits de 1,7 px, dans une grille de 24, inspiré de SF Symbols.
 * Dessinées à la main plutôt qu'importées : une bibliothèque d'icônes complète
 * pèse plus lourd que l'application elle-même, et seules une trentaine sont utiles.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 20, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconHome = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5.5 9.5V20h13V9.5" />
    <path d="M9.5 20v-5.5h5V20" />
  </Icon>
);

export const IconList = (props: IconProps) => (
  <Icon {...props}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <circle cx="3.5" cy="6" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="12" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="18" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconCalendar = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Icon>
);

export const IconCheckSquare = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <path d="M8 12.5l2.8 2.8L16.5 9.5" />
  </Icon>
);

export const IconTasks = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 6h11M9 12h11M9 18h11" />
    <path d="M3 6l1.6 1.6L7 5" />
    <path d="M3 12l1.6 1.6L7 11" />
    <path d="M3 18l1.6 1.6L7 17" />
  </Icon>
);

export const IconWallet = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="6" width="18" height="13" rx="3" />
    <path d="M3 10h18" />
    <circle cx="16.5" cy="14.5" r="1.3" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconGame = (props: IconProps) => (
  <Icon {...props}>
    <rect x="2.5" y="7" width="19" height="10" rx="5" />
    <path d="M7 10.5v3M5.5 12h3M15.5 11h.01M18 13.5h.01" />
  </Icon>
);

export const IconPhoto = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="4.5" width="18" height="15" rx="3" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="M3.5 17l4.8-4.4a2 2 0 0 1 2.7 0l6.4 5.9" />
  </Icon>
);

export const IconTent = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 4 3 20h18L12 4Z" />
    <path d="M12 4v16M9 20l3-6 3 6M2 20h20" />
  </Icon>
);

export const IconDocument = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 17h4" />
  </Icon>
);

export const IconMail = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="5.5" width="18" height="13" rx="3" />
    <path d="M4 7.5l7.1 5.2a1.6 1.6 0 0 0 1.9 0L20 7.5" />
  </Icon>
);

export const IconShield = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3l7.5 3v5.6c0 4.3-3 8.2-7.5 9.4-4.5-1.2-7.5-5.1-7.5-9.4V6z" />
    <path d="M9 12l2.2 2.2L15.5 10" />
  </Icon>
);

export const IconSettings = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.5v2.6M12 18.9v2.6M4.2 7.2l2.2 1.3M17.6 15.5l2.2 1.3M4.2 16.8l2.2-1.3M17.6 8.5l2.2-1.3" />
  </Icon>
);

export const IconCode = (props: IconProps) => (
  <Icon {...props}>
    <path d="M8.5 7.5L4 12l4.5 4.5M15.5 7.5L20 12l-4.5 4.5M13.5 4.5l-3 15" />
  </Icon>
);

export const IconBug = (props: IconProps) => (
  <Icon {...props}>
    <path d="M8.5 7.5a3.5 3.5 0 017 0" />
    <rect x="7" y="7.5" width="10" height="12" rx="5" />
    <path d="M7 11.5H3.5M7 15.5H4M7 19l-2.5 2M17 11.5h3.5M17 15.5h3M17 19l2.5 2" />
  </Icon>
);

export const IconSparkles = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
    <path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
  </Icon>
);

export const IconSearch = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="10.5" cy="10.5" r="7" />
    <path d="M15.6 15.6L21 21" />
  </Icon>
);

export const IconPlus = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const IconClose = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 5l14 14M19 5L5 19" />
  </Icon>
);

export const IconChevronRight = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 5l7 7-7 7" />
  </Icon>
);

export const IconChevronLeft = (props: IconProps) => (
  <Icon {...props}>
    <path d="M15 5l-7 7 7 7" />
  </Icon>
);

export const IconChevronDown = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 9l7 7 7-7" />
  </Icon>
);

export const IconBell = (props: IconProps) => (
  <Icon {...props}>
    <path d="M18 15.5V11a6 6 0 1 0-12 0v4.5L4.5 18h15z" />
    <path d="M9.8 18a2.2 2.2 0 0 0 4.4 0" />
  </Icon>
);

export const IconUsers = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="9" cy="8" r="3.4" />
    <path d="M2.8 19.5c0-3.2 2.8-5.3 6.2-5.3s6.2 2.1 6.2 5.3" />
    <path d="M16.2 5.2a3.4 3.4 0 0 1 0 6.6M17.5 14.6c2.2.5 3.7 2.2 3.7 4.9" />
  </Icon>
);

export const IconClock = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.3l3.4 2" />
  </Icon>
);

export const IconMapPin = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 21.5s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" />
    <circle cx="12" cy="10.2" r="2.6" />
  </Icon>
);

export const IconDownload = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3.5v11M7.5 10.5L12 15l4.5-4.5" />
    <path d="M4.5 18.5h15" />
  </Icon>
);

export const IconUpload = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 16V5M7.5 9L12 4.5 16.5 9" />
    <path d="M4.5 19.5h15" />
  </Icon>
);

export const IconEdit = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 20h4l11-11a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5z" />
    <path d="M14.5 6.5L17.5 9.5" />
  </Icon>
);

export const IconTrash = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7h16" />
    <path d="M9.5 7V4.5h5V7" />
    <path d="M6 7l1 12.5h10L18 7" />
    <path d="M10.5 11v5M13.5 11v5" />
  </Icon>
);

export const IconFilter = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3.5 6h17l-6.5 7.5V20l-4-2v-4.5z" />
  </Icon>
);

export const IconGrip = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="9" cy="6" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="15" cy="6" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="9" cy="12" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="9" cy="18" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="15" cy="18" r="1.3" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconCheck = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4.5 12.5l5 5 10-11" />
  </Icon>
);

export const IconAlert = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3.5l9.2 16H2.8z" />
    <path d="M12 9.5v4.2M12 16.8h.01" />
  </Icon>
);

export const IconOffline = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 3l18 18" />
    <path d="M8.5 13.5a5 5 0 0 1 7 0M5 10.2a9.5 9.5 0 0 1 5-2.6M14 7.6a9.5 9.5 0 0 1 5 2.6" />
    <circle cx="12" cy="17.5" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconRefresh = (props: IconProps) => (
  <Icon {...props}>
    <path d="M20 12a8 8 0 1 1-2.6-5.9" />
    <path d="M20 4v4.5h-4.5" />
  </Icon>
);

export const IconLogout = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 4.5H6.5A2 2 0 0 0 4.5 6.5v11a2 2 0 0 0 2 2H14" />
    <path d="M17 8.5l3.5 3.5-3.5 3.5M20 12h-9" />
  </Icon>
);

export const IconAttachment = (props: IconProps) => (
  <Icon {...props}>
    <path d="M17.5 11.5l-6 6a4.2 4.2 0 0 1-6-6l7-7a3 3 0 0 1 4.3 4.3l-7 7a1.8 1.8 0 0 1-2.6-2.6l6.3-6.3" />
  </Icon>
);

export const IconChart = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 20V4" />
    <path d="M4 20h16" />
    <rect x="7.5" y="12" width="3" height="5" rx="1" />
    <rect x="13" y="8" width="3" height="9" rx="1" />
  </Icon>
);

export const IconCake = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 20h16v-5a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3z" />
    <path d="M12 12V8.5M9 12V9.5M15 12V9.5" />
    <path d="M4 16.5c1.5 1 3 1 4.5 0s3 1 4.5 0 3-1 4.5 0" />
  </Icon>
);

export const IconSun = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" />
  </Icon>
);

export const IconMoon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
  </Icon>
);

/** Pull / t-shirt — onglet Unif / Unis. */
export const IconShirt = (props: IconProps) => (
  <Icon {...props}>
    <path d="M8.5 5.5 12 3l3.5 2.5L20 7l-2 3.5V20H6V10.5L4 7l4.5-1.5z" />
    <path d="M9 20v-5.5a3 3 0 0 1 6 0V20" />
  </Icon>
);

/* ---------------------------------------------------------------------------
 * Icônes ajoutées pour remplacer les emoji de l'app Staff.
 *
 * Dessinées ici plutôt que remplacées par un autre emoji : un emoji est rendu
 * par la police système, change d'aspect d'un OS à l'autre, ne suit ni la
 * couleur ni la taille du thème, et ne peut pas être animé. Côté familles il
 * n'y en avait aucun — c'était la divergence visuelle la plus voyante entre les
 * deux produits.
 * ------------------------------------------------------------------------- */

/** Activité annulée. */
export const IconBan = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M6 6l12 12" />
  </Icon>
);

/** Commentaire sur une tâche. */
export const IconChat = (props: IconProps) => (
  <Icon {...props}>
    <path d="M20.5 12.5c0 3.9-3.8 7-8.5 7-1 0-2-.15-2.9-.42L4 20.5l1.5-3.6A6.6 6.6 0 0 1 3.5 12.5c0-3.9 3.8-7 8.5-7s8.5 3.1 8.5 7z" />
  </Icon>
);

/** Tâche assignée — punaise. */
export const IconPin = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 3h6l-.7 5.2 3.2 3.3H6.5l3.2-3.3z" />
    <path d="M12 11.5V21" />
  </Icon>
);

/** Bibliothèque de jeux — dé. */
export const IconDice = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
    <path d="M8.5 8.5h.01M15.5 8.5h.01M12 12h.01M8.5 15.5h.01M15.5 15.5h.01" />
  </Icon>
);

/** Météo — thermomètre. */
export const IconThermometer = (props: IconProps) => (
  <Icon {...props}>
    <path d="M14 14.8V5.5a2 2 0 1 0-4 0v9.3a4 4 0 1 0 4 0z" />
    <path d="M12 9.5v5.8" />
  </Icon>
);

/** Réponse de présence — main levée. */
export const IconHandRaised = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 11V4.8a1.4 1.4 0 0 1 2.8 0V11" />
    <path d="M11.8 10.4V3.6a1.4 1.4 0 0 1 2.8 0v7.2" />
    <path d="M14.6 11.2V6.4a1.4 1.4 0 0 1 2.8 0v8.1c0 3.4-2.3 6-5.6 6-2.6 0-4.2-1.3-5.4-3.4l-2-3.5a1.4 1.4 0 0 1 2.3-1.6L9 14" />
  </Icon>
);

/**
 * Favori. Le remplissage passe par `fill` et non par un second dessin : c'est
 * la même forme dans les deux états, ce qui permet d'animer la bascule.
 */
export const IconStar = ({ filled = false, ...props }: IconProps & { filled?: boolean }) => (
  <Icon {...props} fill={filled ? 'currentColor' : 'none'}>
    <path d="M12 3.8l2.6 5.3 5.9.85-4.25 4.15 1 5.9L12 17.2l-5.25 2.8 1-5.9L3.5 9.95l5.9-.85z" />
  </Icon>
);

/** Tente scoute — Carnet de camp & séjours. */
export const IconCamp = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 20h18" />
    <path d="M12 4 3 20" />
    <path d="M12 4l9 16" />
    <path d="M12 4v16" />
    <path d="m8.5 20 3.5-6 3.5 6" />
  </Icon>
);

/** Dossier de documents. */
export const IconFolder = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 7v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6.5l-2-2H5a2 2 0 0 0-2 2z" />
  </Icon>
);

/** Nouveau dossier. */
export const IconFolderPlus = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 11v6M9 14h6" />
    <path d="M3 7v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6.5l-2-2H5a2 2 0 0 0-2 2z" />
  </Icon>
);

/** Téléphone / Répertoire. */
export const IconPhone = (props: IconProps) => (
  <Icon {...props}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </Icon>
);

/** SMS / Message texte. */
export const IconMessage = (props: IconProps) => (
  <Icon {...props}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M8 9h8M8 13h5" />
  </Icon>
);

/** WhatsApp. */
export const IconWhatsApp = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
    <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
  </Icon>
);

/** Facebook Messenger. */
export const IconMessenger = (props: IconProps) => (
  <Icon {...props}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    <path d="m8.5 13.5 3-4 2 2 2.5-3.5" />
  </Icon>
);

/** Copier dans le presse-papier. */
export const IconCopy = (props: IconProps) => (
  <Icon {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2.5" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Icon>
);


